import React from 'react';
import {createStyles, Theme, withStyles, WithStyles} from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogActions from '@material-ui/core/DialogActions';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import {injectIntl, IntlShape} from 'react-intl'
import makeStyles from "@material-ui/core/styles/makeStyles";

interface Props {
    titleColor?: string
}

//=======DialogTitle=======
const titleStyles = (theme: Theme) =>
    createStyles({
        root: {
            margin: 0,
            padding: theme.spacing(2),
            backgroundColor: (props: Props) => props.titleColor === 'primary' ? '#0C7CFF' : '#fff',
            color: (props: Props) => props.titleColor ? '#fff' : '#000',
        },

        closeButton: {
            position: 'absolute',
            right: theme.spacing(1),
            top: theme.spacing(1),
            color: (props: Props) => props.titleColor === 'primary' ? theme.palette.grey[50] : theme.palette.grey[500],
        },
    });

export interface DialogTitleProps extends WithStyles<typeof titleStyles> {
    id: string;
    children: React.ReactNode;
    onClose: () => void;
    titleColor?: string
}

const DialogTitle = withStyles(titleStyles)((props: DialogTitleProps) => {
    const {children, classes, onClose} = props;
    return (
        <MuiDialogTitle disableTypography className={classes.root}>
            <Typography variant="h6">{children}</Typography>
            {onClose ? (
                <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
                    <CloseIcon/>
                </IconButton>
            ) : null}
        </MuiDialogTitle>
    );
});

//=======DialogContent=======
const DialogContent = withStyles((theme: Theme) => ({
    root: {
        padding: theme.spacing(2),
    },
    dividers: {
        borderBottom: 0
    }
}))(MuiDialogContent);

//=======DialogActions=======
const DialogActions = withStyles((theme: Theme) => ({
    root: {
        margin: 0,
        padding: theme.spacing(2),
        justifyContent: 'center'
    }
}))(MuiDialogActions);


//=======CustomUIComp=======
interface myDialogStyleProps {
    customWidth?: string
}

const useStyles = makeStyles({
    root: {
        '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0,0,0,0.25)'
        },
        maxWidth: (props: myDialogStyleProps) => props.customWidth ? props.customWidth : 'sm',
        margin: 'auto',
    },
    paper: {
        borderRadius: 2
    }
});

export interface myDialogProps {
    onClose: () => void;
    open: boolean;
    children: React.ReactNode;
    styleProps: myDialogStyleProps;
}

function MyDialog(props: myDialogProps) {
    let {styleProps, children, ...rest} = props
    const classes = useStyles(styleProps);
    return <Dialog className={classes.root}
                   scroll="body"
                   fullWidth
                   disableBackdropClick={true}
                   aria-labelledby="customized-dialog-title"
                   {...rest}>{children}</Dialog>
}


//=====Button====
const PrimaryButton = withStyles((theme: Theme) =>
    createStyles({
        root: {
            backgroundColor: '#0C7CFF',
            color: '#fff',
            '&:hover': {
                backgroundColor: '#0b71e1',
            }
        }
    })
)(Button)

const WhiteButton = withStyles((theme: Theme) =>
    createStyles({
        root: {
            backgroundColor: '#fff',
            color: '#1D3155',
            border: '1px solid #CDD3DC',
            marginRight: theme.spacing(1),
            '&:hover': {
                borderColor: '#0C7CFF',
                backgroundColor: '#fff'
            }
        }
    })
)(Button)

interface CustomDialogsProps {
    intl: IntlShape,
    open: boolean,
    handleClose: () => void,
    title: string,
    content: React.ReactNode,
    onConfirm?: () => void,
    showActions?: boolean,
    titleColor?: string,
    customWidth?: string,
}

CustomDialog.defaultProps = {
    showActions: true
}

function CustomDialog(props: CustomDialogsProps) {
    const {intl} = props
    return (
        <MyDialog onClose={props.handleClose}
                  styleProps={{customWidth: props.customWidth}}
                  open={props.open}>
            <DialogTitle id="customized-dialog-title"
                         onClose={props.handleClose}
                         titleColor={props.titleColor}>
                {props.title}
            </DialogTitle>
            <DialogContent dividers>
                {props.content}
            </DialogContent>
            {props.showActions ?
                <DialogActions>
                    <WhiteButton onClick={props.handleClose}>
                        {intl.formatMessage({id: 'cancel'})}
                    </WhiteButton>
                    <PrimaryButton onClick={props.onConfirm}>
                        {intl.formatMessage({id: 'confirm'})}
                    </PrimaryButton>
                </DialogActions> : null}
        </MyDialog>
    );
}

export default injectIntl(CustomDialog)